import { BelowMapSetter } from '../layouts/BelowMapContext';
import React, { useMemo, useState } from 'react';
import { MaintenanceCalendar } from './MaintenanceCalendar';
import { BelowMapTab } from '../layouts/BelowMapTab';
import {
  useGetExtractedNoticesStatusQuery,
  useGetTSPsQuery,
} from '../services/edgeApi';
import { useSelector } from 'react-redux';
import { selectTspOptions } from '../map/slice/mapSelectors';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { DateSelector } from '../helpers/DateSelector';
import { useViewMode } from '../hooks/useViewMode';

import {
  MaintenanceCalendarGroupBy,
  MaintenanceCalendarView,
} from './MaintenanceCalendar';
import {
  NoticeDisplaySeverity,
  NoticeSourceType,
} from '../types/api/NoticeRecord';
import { titleize } from '../utils/stringUtils';
import { DevelopmentFlag } from '../helpers/DevelopmentFlag';
import { colors, interpolateColor } from '../utils/colors';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { toDateString, toIsoDate } from '../utils/dateUtils';
import { NoticeStatus } from '@/types/api/ExtractedNotice';
import { ChevronDownIcon, ChevronUpIcon } from '@iconicicons/react';

const sourceTypeOptions = Object.values(NoticeSourceType).map((sourceType) => ({
  label: titleize(sourceType.replaceAll('_', ' ')),
  value: sourceType,
}));

const severityOptions = Object.values(NoticeDisplaySeverity).map(
  (severity) => ({
    label: severity,
    value: severity,
  }),
);
const LAST_YEAR = new Date();
LAST_YEAR.setFullYear(LAST_YEAR.getFullYear() - 1);

const compareRecords = (
  a: NoticeStatus,
  b: NoticeStatus,
  sortBy: keyof NoticeStatus,
) => {
  let av = a[sortBy];
  let bv = b[sortBy];

  // Treat null numeric values as -1.
  if (bv !== null && bv !== undefined && (av === null || av === undefined)) {
    return -1;
  }

  if (av !== null && av !== undefined && (bv === null || bv === undefined)) {
    return 1;
  }

  if (typeof av === 'number' && typeof bv === 'number') {
    return av - bv;
  }

  if (typeof av === 'string' && typeof bv === 'string') {
    return av.localeCompare(bv);
  }

  return 0;
};

export const MaintenancePage = () => {
  useGetTSPsQuery();
  const [viewMode] = useViewMode();
  const tspOptions = useSelector(selectTspOptions);
  const [filteredTspIds, setFilteredTspIds] = useState<number[]>([]);
  // const [startDate, setStartDate] = useDebouncedState(new Date(), 500);
  const [columnUnit, setColumnUnit] = useState<MaintenanceCalendarView>(
    MaintenanceCalendarView.DAY,
  );
  const [groupBy, setGroupBy] = useState<MaintenanceCalendarGroupBy>(
    MaintenanceCalendarGroupBy.PIPELINE,
  );
  const [sourceTypes, setSourceTypes] = useState<NoticeSourceType[]>([]);
  const [severity, setSeverity] = useState<NoticeDisplaySeverity[]>([]);
  const [startNoticesDate, setStartNoticesDate] = useState<Date>(new Date());
  const [startStatusDate, setStartStatusDate] = useState<Date>(LAST_YEAR);
  const [sortBy, _setSortBy] = useState<keyof NoticeStatus>('tsp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const setSortBy = (field: keyof NoticeStatus) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      _setSortBy(field);
      setSortDirection('asc');
    }
  };

  const { data: extractionStatus, isLoading } =
    useGetExtractedNoticesStatusQuery(
      { startDate: toIsoDate(startStatusDate) },
      {
        pollingInterval: 60_000,
        refetchOnFocus: true,
      },
    );

  const dateInputDefaultProps = useMemo(
    () => ({
      defaultValue: toIsoDate(startNoticesDate),
    }),
    [startNoticesDate],
  );

  const formatPercent = (value: number | null) =>
    value == null || value === 0 ? '--' : `${(value * 100).toFixed(1)}%`;

  const formatNumber = (value: number | null) =>
    value == null ? '--' : value.toString();

  const formatLastUpdate = (value: string | null) => {
    if (!value) return '--';

    const date = new Date(value);

    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    });

    const day = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York',
    });

    return `${time}  ${day}`;
  };

  const formatPostDate = (value: string | null) =>
    value ? toDateString(value) : '--';

  return (
    <>
      <h2>Notice Calendar</h2>
      <div className="d-flex flex-column gap-3 mb-3">
        <DateSelector
          label="Starting date"
          inputProps={dateInputDefaultProps}
          onChange={(evt) =>
            setStartNoticesDate(evt.target.valueAsDate ?? new Date())
          }
        />
        <MultiSelectSearch
          options={severityOptions}
          value={severity}
          onChange={(selection) =>
            setSeverity(selection.map((option) => option.value))
          }
          placeholder="Notice Severity"
        />
        <MultiSelectSearch
          options={sourceTypeOptions}
          value={sourceTypes}
          onChange={(selection) =>
            setSourceTypes(selection.map((option) => option.value))
          }
          placeholder="Notice Source Type"
        />
        <MultiSelectSearch
          options={tspOptions}
          onChange={(selection) =>
            setFilteredTspIds(selection.map((option) => option.value))
          }
          placeholder="Pipelines"
        />
        {viewMode === 'development' && (
          <>
            <div className="input-group">
              <span className="input-group-text">
                <DevelopmentFlag className="me-2" />
                View
              </span>
              <select
                className="form-select"
                value={columnUnit}
                onChange={(e) =>
                  setColumnUnit(e.target.value as MaintenanceCalendarView)
                }
              >
                <option value={MaintenanceCalendarView.DAY}>Days</option>
                <option value={MaintenanceCalendarView.WEEK}>Weeks</option>
                <option value={MaintenanceCalendarView.MONTH}>Months</option>
              </select>
            </div>
            <div className="input-group">
              <span className="input-group-text">
                <DevelopmentFlag className="me-2" />
                Group By
              </span>
              <select
                className="form-select"
                value={groupBy}
                onChange={(e) =>
                  setGroupBy(e.target.value as MaintenanceCalendarGroupBy)
                }
              >
                <option value={MaintenanceCalendarGroupBy.LOCATION}>
                  Location
                </option>
                <option value={MaintenanceCalendarGroupBy.PIPELINE}>
                  Pipeline
                </option>
                <option value={MaintenanceCalendarGroupBy.ZONE} disabled>
                  Zone
                </option>
              </select>
            </div>
          </>
        )}
      </div>
      <BelowMapSetter>
        <BelowMapTab tabName="Notices Calendar" contextKey="notices-calendar">
          <MaintenanceCalendar
            tspIds={filteredTspIds}
            startDate={startNoticesDate}
            columnUnit={columnUnit}
            groupBy={groupBy}
            sourceTypes={sourceTypes}
          />
        </BelowMapTab>
        <BelowMapTab tabName="Notices Status" contextKey="scraping-status">
          <div className="container-fluid">
            <div className="row my-2">
              <div className="col-6">
                <div className="input-group">
                  <label className="input-group-text">Starting date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toIsoDate(startStatusDate)}
                    onChange={(e) =>
                      setStartStatusDate(e.target.valueAsDate ?? LAST_YEAR)
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <table className="table">
                <thead className="sticky-top">
                  <tr>
                    <th
                      // className="text-center"
                      role="button"
                      onClick={() => setSortBy('tsp')}
                    >
                      TSP{' '}
                      {sortBy === 'tsp' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('last_update')}
                    >
                      Last Update (ET){' '}
                      {sortBy === 'last_update' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('delay_minutes')}
                    >
                      Elapsed (M){' '}
                      {sortBy === 'delay_minutes' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('notices')}
                    >
                      Notices In Range{' '}
                      {sortBy === 'notices' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('relevant')}
                    >
                      Relevant{' '}
                      {sortBy === 'relevant' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('processed_ratio')}
                    >
                      Processed{' '}
                      {sortBy === 'processed_ratio' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('complete_ratio')}
                    >
                      Complete{' '}
                      {sortBy === 'complete_ratio' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('post_date')}
                    >
                      Last Post{' '}
                      {sortBy === 'post_date' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>

                    <th
                      className="text-center"
                      role="button"
                      onClick={() => setSortBy('nomination_count')}
                    >
                      Katana Noms{' '}
                      {sortBy === 'nomination_count' &&
                        (sortDirection === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronUpIcon />
                        ))}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center align-items-center">
                          <LoadingIndicator />
                        </div>
                      </td>
                    </tr>
                  ) : (
                    extractionStatus
                      ?.slice()
                      .sort(
                        (a, b) =>
                          compareRecords(a, b, sortBy) *
                          (sortDirection === 'asc' ? 1 : -1),
                      )
                      .map((row) => {
                        const processedColor = interpolateColor(
                          colors.matteRed,
                          colors.matteGreen,
                          row.processed_ratio ?? 0,
                        );

                        const completedColor = interpolateColor(
                          colors.matteRed,
                          colors.matteGreen,
                          row.complete_ratio ?? 0,
                        );

                        return (
                          <tr key={row.tsp}>
                            <td>{row.tsp}</td>

                            <td className="text-center">
                              {formatLastUpdate(row.last_update)}
                            </td>

                            <td className="text-center">
                              {formatNumber(row.delay_minutes)}
                            </td>

                            <td className="text-center">{row.notices}</td>

                            <td className="text-center">{row.relevant}</td>

                            <td
                              className="text-center"
                              style={{ color: processedColor }}
                            >
                              {formatPercent(row.processed_ratio)}
                            </td>

                            <td
                              className="text-center"
                              style={{ color: completedColor }}
                            >
                              {formatPercent(row.complete_ratio)}
                            </td>

                            <td className="text-center">
                              {formatPostDate(row.post_date)}
                            </td>

                            <td className="text-center">{row.nomination_count}</td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </BelowMapTab>
      </BelowMapSetter>
    </>
  );
};
